import type { QueryClient } from '@tanstack/react-query'

/**
 * Mutations that share a `scope.id` run one at a time, in the order they were made.
 * Without one they all run at once — `resumePausedMutations` continues every paused
 * mutation in parallel — so a stack of writes made offline would hit the server in
 * whatever order it happened to accept them, and the user's last edit would not
 * reliably be the one that stuck.
 *
 * Scope only the writes that need ordering against each other: everything in a scope
 * queues behind everything else in it, so a scope covering more than the rows one list
 * query holds makes unrelated work wait out a slow queue. Build ids from the resource
 * being written, not from the mutation alone.
 */
export type MutationScope = { id: string }

/**
 * Whether the mutation settling right now is the last one queued in its scope. Every
 * queued write settles into its own `invalidateQueries`, and a refetch between them
 * paints the server's older state back over the optimistic rows the writes still
 * waiting their turn have already applied — so only the last write of a queue refetches.
 *
 * Counts the settling mutation itself: query-core runs `onSettled` before marking the
 * mutation succeeded, so the caller is still `pending` here. Nutmeg's `offlineEditItem`
 * tests pin that: a change to it would silently let the second-to-last write refetch.
 */
export function isLastQueuedMutation(queryClient: QueryClient, scope: MutationScope) {
  const queued = queryClient.isMutating({
    predicate: (mutation) => mutation.options.scope?.id === scope.id,
  })

  return queued <= 1
}
